/** Dependencies */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';

/** Styles */
import {
  alpha,
  AppBar,
  Box,
  styled,
  Typography,
  Toolbar,
  Grid,
  Button,
  useMediaQuery,
} from '@mui/material';
import FormDatePicker from '../../../../components/FormDatePicker';
import FormSelect from '../../../../components/FormSelect';
import useExpenseCategories from '../../../../hooks/useExpenseCategories';
import useExpenses from '../../../../hooks/useExpenses';
import keyHasPermission from '../../../../helpers/keyHasPermission';
import useProfile from '../../../../hooks/useProfile';

/** helpers */
import getActiveLanguage from '../../../../helpers/getActiveLanguage';

const SearchComponent = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
    width: '100%',
  },
}));

interface Props {
  select: string[] | null;
  setSelect: React.Dispatch<React.SetStateAction<string[] | null>>;
  startDate: string;
  setStartDate: (value: ((prevState: string) => string) | string) => void;
  endDate: string;
  setEndDate: React.Dispatch<React.SetStateAction<string>>;
}

function CustomerSearch({
  select,
  setSelect,
  endDate,
  startDate,
  setEndDate,
  setStartDate,
}: Props) {
  const { t } = useTranslation('search');
  const { meProfile } = useProfile();

  const { expenseCategories } = useExpenseCategories();
  const { fetchExpenses } = useExpenses();
  const matchesXs = useMediaQuery((theme: any) => theme.breakpoints.down('md'));

  const date = new Date(Date.now());
  date.setDate(date.getDate() - 7);

  const initialStartDate = format(date, 'yyyy-MM-dd');
  const initialEndDate = format(new Date(Date.now()), 'yyyy-MM-dd');

  const handleSubmit = () => {
    const collectStrings = `${startDate},${endDate}`;

    if (select) {
      fetchExpenses(1, collectStrings, select);
    } else {
      fetchExpenses(1, collectStrings);
    }
  };

  useEffect(() => {
    setStartDate(initialStartDate);
    setEndDate(initialEndDate);
  }, [expenseCategories]);

  const hasListPermission = keyHasPermission(
    'view-expense-listing',
    meProfile?.permissions || [],
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar
        position="static"
        sx={{
          backgroundColor: 'white',
          marginBottom: '25px',
        }}
      >
        <Toolbar>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ flexGrow: 1, display: { xs: 'block', sm: 'block' } }}
          >
            <SearchComponent>
              <Grid container justifyContent="left" p={1}>
                <Grid item xs={12} sm={6} md={3} lg={2} p={1}>
                  <FormDatePicker
                    value={startDate}
                    onChange={(event: any) => setStartDate(event.target.value)}
                    sx={{ color: 'black' }}
                    label={t('filters.date')}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2} p={1}>
                  <FormDatePicker
                    value={endDate}
                    onChange={(event: any) => setEndDate(event.target.value)}
                    sx={{ color: 'black' }}
                    label={t('filters.end_date')}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2} p={1}>
                  <FormSelect
                    underline
                    multiple
                    label={t('filters.cost')}
                    placeholder={t('filters.cost_type')}
                    sx={{ color: 'black' }}
                    options={expenseCategories.map(item => ({
                      id: item.id,
                      text: `${getActiveLanguage(item?.translations)?.name}`,
                    }))}
                    value={select || []}
                    // @ts-ignore
                    onChange={(event: any) => setSelect(event.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2} p={1}>
                  <Button
                    onClick={handleSubmit}
                    sx={{
                      marginTop: { xs: '10px', sm: '30px' },
                      width: '100%',
                    }}
                    size="large"
                    variant="contained"
                    disabled={!hasListPermission}
                  >
                    {t('filters.name')}
                  </Button>
                </Grid>
              </Grid>
            </SearchComponent>
          </Typography>
        </Toolbar>
      </AppBar>
    </Box>
  );
}

export default CustomerSearch;
